/**
 * Payment review — bookings that should have been paid for and have no payment
 * recorded, each with whatever PayPal payments look like they belong to it.
 *
 * From docs/booking-api.swagger.json. The list is stored on the API, not
 * recomputed on open: the version before it rebuilt the list from a rolling age
 * window every time, so a booking older than the window vanished from the
 * screen while the money was still missing, and an item nobody had looked at
 * was indistinguishable from one somebody had already decided about.
 */

/** `PayPalCaptureCandidateDto` — a payment that looks like it belongs here. */
export interface PayPalCandidate {
  transactionId: string;
  invoiceId: string | null;
  payerEmail: string | null;
  amount: number | null;
  transactionDate: string | null;
  /** Attaching it here takes it away from the other booking it fits. */
  alsoFitsOtherBookings: boolean;
}

/** `BookingPaymentReviewItemDto` — one booking awaiting a decision. */
export interface PaymentReviewItem {
  reviewId: string;
  bookingId: string;
  bookedAt: string | null;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  eventName: string | null;
  timeslotStartTime: string | null;
  price: number | null;
  bookingStatus: number | null;
  /** An open payment request exists, so this may simply not have been paid yet. */
  hasUnpaidPurchase: boolean;
  flaggedAt: string | null;
  lastSearchedAt: string | null;
  searchCount: number | null;
  notes: string | null;
  candidates: PayPalCandidate[] | null;
  /** Ruling a payment out is remembered, so the same wrong suggestion is not re-offered. */
  rejectedTransactionIds: string[] | null;
}

/** `BookingPaymentReviewListDto`. */
export interface PaymentReviewList {
  open: number | null;
  withCandidates: number | null;
  withoutCandidates: number | null;
  amountAtStake: number | null;
  lastSearchedAt: string | null;
  recentlyResolved: number | null;
  items: PaymentReviewItem[] | null;
}

/** `UnmatchedBookingSweepResultDto` — what one PayPal search turned up. */
export interface SweepResult {
  windowStart: string | null;
  windowEnd: string | null;
  candidateBookings: number | null;
  transactionsScanned: number | null;
  alreadyRecorded: number | null;
  matched: number | null;
  applied: number | null;
  ambiguous: number | null;
  autoApplyEnabled: boolean;
  matches: unknown[] | null;
  stillUnmatched: unknown[] | null;
}

/** `BookingArchiveResultDto` — what closing out the old backlog would do. */
export interface ArchiveResult {
  before: string | null;
  matched: number | null;
  amount: number | null;
  oldestBooking: string | null;
  newestBooking: string | null;
  applied: boolean;
}

/**
 * `BookingReconciliationMissesDto`.
 *
 * A match needs payer email, amount and timing to agree at once, so a bare
 * "nothing found" total says nothing about which of the three to go and fix.
 */
export interface ReconcileMisses {
  noPaymentFromPayer: number | null;
  payerFoundWrongAmount: number | null;
  payerAndAmountOutsideWindow: number | null;
  rightAmountAndTimeWrongPayer: number | null;
  closestOutsideWindowHours: number | null;
}

/** `BookingReconciliationRowDto`. */
export interface ReconcileRow {
  bookingId: string;
  bookedAt: string | null;
  userEmail: string | null;
  userName: string | null;
  eventName: string | null;
  timeslotStartTime: string | null;
  price: number | null;
  /** One of `benefit | customId | evidence | ambiguous | none`. */
  outcome: string | null;
  applied: boolean;
  transactionId: string | null;
  amount: number | null;
  note: string | null;
}

/** `BookingReconciliationResultDto`. */
export interface ReconcileResult {
  from: string | null;
  to: string | null;
  examined: number | null;
  benefitsRelinked: number | null;
  matchedByCustomId: number | null;
  matchedByEvidence: number | null;
  applied: number | null;
  ambiguous: number | null;
  unresolved: number | null;
  transactionsScanned: number | null;
  /** Proof the double-spend guard ran, rather than a silent filter nobody can audit. */
  alreadyRecorded: number | null;
  autoApplyEnabled: boolean;
  matchWindowHours: number | null;
  misses: ReconcileMisses | null;
  warnings: string[] | null;
  rows: ReconcileRow[] | null;
}

const OUTCOME_LABEL: Record<string, string> = {
  benefit: 'paymentReview.outcomeBenefit',
  customId: 'paymentReview.outcomeCustomId',
  evidence: 'paymentReview.outcomeEvidence',
  ambiguous: 'paymentReview.needsAChoice',
  none: 'paymentReview.outcomeNone',
};

export function outcomeLabelKey(outcome: string | null | undefined) {
  return OUTCOME_LABEL[outcome ?? ''] ?? 'paymentReview.outcomeNone';
}
